import React, {ClipboardEvent, useEffect, useState} from 'react';
import {EventUserRegistration, Result} from "../interfaces";
import {sendContact} from "../DAL/data-service";
import {getIsChina} from "../../../domain/hidden-data-provider";
import {
    createControl,
    removeInvisibleCharsOnPaste,
    updateControl,
    validateControl,
    validateForm
} from "../../../domain/validation/validation";
import {getSimpleCaptchaValidationError} from "../../../domain/captcha/simple-captcha";
import {IFormControl} from "../../../ui/forms/interfaces";
import {
    getRecaptcha2Error,
    getRecaptchaToken,
    renderRecaptcha2,
    resetRecaptcha2
} from "../../../domain/captcha/recaptcha-provider";
import ChinaCaptchaField from "../../../ui/forms/fields/captcha-china-field";
import Recaptcha3Text from "../../../domain/captcha/recaptcha3-terms";
import FormTextField from "../../../ui/forms/fields/text-field";
import {IServerValidation} from "../../../domain/validation/interfaces";
import FormTextAreaField from "../../../ui/forms/fields/textarea-field";
import {getPageNodeGUID} from "../../../domain/common";
import {sendGAEvent} from "../../../domain/marketing/gtm-provider";

interface IEventsContactProps {
    dataUser: EventUserRegistration,
}


/**
 * the entry point
 */
const EventsContactApp= (props: IEventsContactProps) => {
    const isChina = getIsChina();

    // validation ---------------------------------------
    const emailControl = createControl('email', props.dataUser.email, {
        required: true,
        email: true
    });
    const firstNameControl = createControl('firstName', props.dataUser.firstName, {
        required: true
    });
    const lastNameControl = createControl('lastName', props.dataUser.lastName, {
        required: true
    });

    const messageControl = createControl('message', props.dataUser.message, {
        required: true
    });
    const simpleCaptchaControl = createControl('simpleCaptcha', '', {
        required: true,
        requiredMessage: getSimpleCaptchaValidationError(),
        length: 5,
    });
    const [isValid, setIsValid] = useState<any>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [response, setResponse] = useState<IServerValidation|null>();
    const [ResultData, setResultData] = useState<Result>();

    const [email, setEmail] = useState<IFormControl>(emailControl);
    const [firstName, setFirstName] = useState<IFormControl>(firstNameControl);
    const [lastName, setLastName] = useState<IFormControl>(lastNameControl);
    const [message, setMessage] = useState<IFormControl>(messageControl);

    // simple captcha - used for china
    const [simpleCaptchaCode, setSimpleCaptchaCode] = useState<IFormControl>(simpleCaptchaControl);
    const [simpleCaptchaAttempts, setSimpleCaptchaAttempts] = useState(0);

    // google recaptcha version 2 & 3
    const [recaptchaVersion, setRecaptchaVersion] = useState(3);
    const [recaptcha2WidgetID, setRecaptcha2WidgetID] = useState(0);

    // captcha: common
    const [captchaError, setCaptchaError] = useState('');

    /**
     * load initial data (drop-downs content) on page load once
     */
    useEffect(() => {
        (async () => {
            setIsLoading(false);
        })();
    }, []);

    /**
     * handle save changes
     */
    const contactSend = async () => {
        const captchaToken = !isChina ? await getRecaptchaToken(recaptchaVersion) : simpleCaptchaCode.value;

        if(!captchaToken){
            setCaptchaError(isChina ? getSimpleCaptchaValidationError() : getRecaptcha2Error());
            setIsLoading(false);
            return;
        }

        // client side validation
        const isValidate =
            isChina ?
                validateForm([
                    [firstName, setFirstName],
                    [lastName, setLastName],
                    [email, setEmail],
                    [message, setMessage],
                    [simpleCaptchaCode, setSimpleCaptchaCode],
                ]) :
                validateForm([
                    [firstName, setFirstName],
                    [lastName, setLastName],
                    [email, setEmail],
                    [message, setMessage],
                ]);

        setIsValid(isValidate);
        if(!isValidate) return;

        var urlEventId : any = window.location.pathname.split('/').splice(-2, 1)[0];
        var urlEventGuid : any = window.location.pathname.split('/').splice(-1, 1)[0];
        const isNumberId = !isNaN(Number(urlEventId));
        const isNumberGuid = !isNaN(Number(urlEventGuid));
        if (!isNumberId && isNumberGuid) {
            urlEventId = window.location.pathname.split('/').splice(-1, 1)[0];
            urlEventGuid = null;
        }
        else urlEventId = 0;

        const userSendContact: EventUserRegistration = {
            firstName : firstName.value,
            lastName : lastName.value,
            email : email.value,
            message: message.value.replace(/=/g, '&equals;'),
            rSVP_GUID:urlEventGuid,
            eventID: urlEventId,
            userID: 0,
            subEventID: 0,
            pageNodeGUID: getPageNodeGUID(),
            captchaCode: captchaToken,
            recaptchaVersion: recaptchaVersion
        };

        setIsLoading(true);
        const response = await sendContact(userSendContact);
        setIsLoading(false);

        if(!response || response.statusCode !== 200) {
            //setError(response.data as unknown as ModelServerErrors);
            return;
        }

        //------------------------------------------------------------------------recaptcha-error
        if(isChina){

            // re-render simple captcha
            setSimpleCaptchaAttempts(simpleCaptchaAttempts + 1);
        }
        else{
            if(response?.data.resultObjectType === 'recaptcha-error') {
                setRecaptchaVersion(2);
                renderCaptcha2Fallback();
            }

            if(recaptchaVersion === 2){
                resetRecaptcha2();
            }
        }
        //------------------------------------------------------------------------Message if success
        setResultData(response.data);

        //------------------------------------------------------------------------Error message

    };
    /**
     * if recaptcha 3 returns low score (the limit is defined in kentico settings),
     * then recaptcha 2 fallback should be rendered inside <div id="recaptcha"></div>
     */
    const renderCaptcha2Fallback = () => {
        if(!window.grecaptcha) return;

        window.grecaptcha.ready(async () => {
            const widgetID = await renderRecaptcha2('recaptcha');
            setRecaptcha2WidgetID(widgetID);
        });
    };



    /**
     * empty errors on click
     */
    const handleFieldClick = () =>{
        setResponse(null);
        setCaptchaError('');
    };

    return (
        <div className="my-4 waves-box-md mx-auto">

            { ResultData?.resultObjectType !== "SENTSUCCESS" &&
                <>
                    {
                        /* ------------- google recaptcha version 2 or 3 ------------- */
                        !isChina &&
                        <div id="recaptcha" className={`mb-4 flex justify-center recaptcha recaptcha-version-${ recaptchaVersion }`}/>
                    }

                    {
                        /* ------------- china simple captcha ------------- */
                        isChina &&
                        <ChinaCaptchaField
                            control={ simpleCaptchaCode }
                            setSimpleCaptchaCode={ setSimpleCaptchaCode }
                            simpleCaptchaAttempts={ simpleCaptchaAttempts }
                        />
                    }
                    {
                        captchaError &&
                        <div className="text-sm text-red my-4 text-center" data-id={ recaptcha2WidgetID }>{ captchaError }</div>
                    }
                    <div>
                        <FormTextField
                            id={ 'first-name-tb' }
                            label={ 'First Name' }
                            control={ firstName }
                            maxLength={ 100 }
                            onChange={ evt => {
                                const $target = evt.target as HTMLInputElement;
                                setFirstName(updateControl(firstName, $target.value, !firstName.isValid));
                                setResponse(null); // empty server error
                                setCaptchaError('');
                            }}
                            onBlur={ evt => {
                                setFirstName(validateControl(firstName));
                            }}
                            onClick={ handleFieldClick }
                            /*onKeyUp={ handleEnter }*/
                            onPaste={ (evt: ClipboardEvent<HTMLInputElement>) => {
                                setFirstName(updateControl(firstName, removeInvisibleCharsOnPaste(evt)));
                            }}
                        />

                        <FormTextField
                            id={ 'last-name-tb' }
                            label={ 'Last Name' }
                            control={ lastName }
                            maxLength={ 100 }
                            onChange={ evt => {
                                const $target = evt.target as HTMLInputElement;
                                setLastName(updateControl(lastName, $target.value, !lastName.isValid));
                                setResponse(null); // empty server error
                                setCaptchaError('');
                            }}
                            onBlur={ evt => {
                                setLastName(validateControl(lastName));
                            }}
                            onClick={ handleFieldClick }
                            /*onKeyUp={ handleEnter }*/
                            onPaste={ (evt: ClipboardEvent<HTMLInputElement>) => {
                                setLastName(updateControl(lastName, removeInvisibleCharsOnPaste(evt)));
                            }}
                        />
                        <FormTextField
                            id={ 'email-tb' }
                            label={ 'Email' }
                            control={ email }
                            maxLength={ 80 }
                            onChange={ evt => {
                                const $target = evt.target as HTMLInputElement;
                                setEmail(updateControl(email, $target.value, !email.isValid));
                                setResponse(null); // empty server error
                                setCaptchaError('');
                            }}
                            onBlur={ evt => {
                                setEmail(validateControl(email));
                            }}
                            onClick={ handleFieldClick }
                            /*onKeyUp={ handleEnter }*/
                            onPaste={ (evt: ClipboardEvent<HTMLInputElement>) => {
                                setEmail(updateControl(email, removeInvisibleCharsOnPaste(evt)));
                            }}
                        />
                        <FormTextAreaField
                            id={ 'message-tb' }
                            label={ 'Message' }
                            control={ message }
                            maxLength={ 524288 }
                            cols={4}
                            rows={7}
                            focus={ true }
                            onChange={ evt => {
                                const $target = evt.target as HTMLInputElement;
                                setMessage(updateControl(message, $target.value, !message.isValid));
                                setResponse(null); // empty server error
                                setCaptchaError('');

                            }}
                            onBlur={ evt => {
                                setMessage(validateControl(message));
                            }}
                        />
                        {
                            !isLoading  &&
                            <>
                                <button type="button" className="w-btn p-4"
                                    onClick={evt => {
                                    contactSend();
                                }}>Send Message
                                </button>
                            </>

                        }
                        {
                            isLoading &&
                            <>
                                <button type="button" className="w-btn bg-gray-400 p-4"
                                       >Sending...
                                </button>
                            </>

                        }
                    </div>

                    {ResultData?.resultStatus  && ResultData?.message != null && ResultData?.message.toString() !== "" &&

                                <div className="mt-4 ok-box" dangerouslySetInnerHTML={{ __html: ResultData?.message?.toString() }} />

                    }
                    {
                        !isChina && recaptchaVersion === 3 &&
                        <div className="mt-4 text-center">
                            <Recaptcha3Text />
                        </div>
                    }
                </>
            }

            {ResultData?.resultStatus && ResultData.resultObjectType === "SENTSUCCESS" && ResultData?.message != null && ResultData?.message.toString() !== "" &&

                <div className="mt-4 ok-box" dangerouslySetInnerHTML={{ __html: ResultData?.message?.toString() }} />

            }

        </div>
    )
};

export default EventsContactApp;